import type {
  ICapitalLetter,
  IIssueBoardApi,
  IIssueBoardBase,
  IIssueBoardDBSchema,
  IMatrixConfig,
  IMatrixItem,
  IRisikoProbability,
} from "./schemas/issueBoardSchema.js";

export {
  formatIssueFormulaGeneratedString,
  getGeneratedStringForFormulaOnAllKeys,
  getIssuesWithFormulaAdded,
  issueBoardContainsAutomaticFormula,
  issueBoardContainsFormula,
} from "./issueFormulaUtils.js";
export type {
  IAllOtherAttributeFormulaWithGeneratedStrings,
  ICountersForAllAttributesWithFormula,
  IFormulaIssue,
} from "./issueFormulaUtils.js";

const startOfDay = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), date.getDate());
const endOfDay = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
const addDays = (date: Date, days: number): Date =>
  new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate() + days,
    date.getHours(),
    date.getMinutes(),
    date.getSeconds(),
    date.getMilliseconds(),
  );
const endOfWeek = (date: Date): Date => endOfDay(addDays(startOfDay(date), 6 - date.getDay()));
const startOfIsoWeek = (date: Date): Date => {
  const day = date.getDay() || 7;
  return startOfDay(addDays(date, 1 - day));
};

//Example: Replace "Total -> TOT, Dreike -> DR" in string "Blabla Total Dreike" -> "Blabla TOT DR". valuesToReplace={TOTAL: "TOT", DREIKE: "DR"}
export const replaceMultipleValuesInString = (value: string, valuesToReplace: Record<string, string>): string => {
  if (value == null) {
    return "";
  }

  if (value.includes("Pr")) {
    value = value.replace(/Pr[^ ]*$/, "Pr");
  }

  // Loopa genom varje nyckel i valuesToReplace-objektet och ersätt varje förekomst
  Object.keys(valuesToReplace).forEach((searchValue) => {
    const replaceValue = valuesToReplace[searchValue];
    value = value.replace(new RegExp(searchValue, "g"), replaceValue);
  });

  return value;
};

export const getUniqValues = <T>(arr: T[], key: string): T[] => {
  if (arr == null) {
    return [];
  }

  return arr.reduce((acc: T[], item: T) => {
    if (acc.find((i) => getValueAtPath(i, key) === getValueAtPath(item, key)) == null) {
      acc.push(item);
    }
    return acc;
  }, []);
};

const getValueAtPath = (obj: unknown, path: string): unknown => {
  if (obj == null || typeof path !== "string") {
    return undefined;
  }

  return path.split(".").reduce<unknown>((acc, key) => {
    if (acc == null || typeof acc !== "object") {
      return undefined;
    }

    return (acc as Record<string, unknown>)[key];
  }, obj);
};

export const getDeeplyNestedValue = (obj: any, path: string): string | undefined => {
  if (obj == null || typeof path !== "string") {
    return undefined;
  }
  const keys = path.split(".");
  if (keys.length === 1) {
    return obj[path];
  }
  const result = keys.reduce((acc, key) => {
    if (acc == null || typeof acc !== "object") {
      return undefined;
    }
    return acc[key];
  }, obj);
  return typeof result === "string" ? result : undefined;
};

export const normalizeId = (value: unknown): string => {
  if (value == null) return "";
  if (typeof value === "string" || typeof value === "number") return value.toString();
  if (typeof value === "object") {
    const idValue = value as {
      _id?: unknown;
      id?: unknown;
      $oid?: unknown;
      toHexString?: () => string;
      toString?: () => string;
    };

    if (typeof idValue.toHexString === "function") return idValue.toHexString();
    if (idValue.$oid != null) return normalizeId(idValue.$oid);
    if (idValue._id != null) return normalizeId(idValue._id);
    if (idValue.id != null) return normalizeId(idValue.id);
    if (typeof idValue.toString === "function" && idValue.toString !== Object.prototype.toString) {
      return idValue.toString();
    }
  }

  return "";
};

export const formatShortDate = (dateStr: string | Date | null | undefined, hideOldDate = false): string => {
  try {
    if (dateStr == null) {
      return "";
    }

    const date = new Date(dateStr);

    if (isNaN(date.getTime())) {
      return String(dateStr);
    }

    if (hideOldDate === true) {
      if (date < new Date("2000-01-01")) {
        return "";
      }
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${day}.${month}.${year}`;
  } catch (error) {
    console.error("Error formatting date:", error);
    return dateStr == null ? "" : String(dateStr);
  }
};

interface IIssueWithRisiko {
  risiko?: {
    probability?: string | null;
    probabilityAfterTiltak?: string | null;
    consequence?: Record<string, string | null | undefined> | null;
    consequenceAfterTiltak?: Record<string, string | null | undefined> | null;
  } | null;
}

interface IGetMatrixArgs {
  issue: IIssueWithRisiko;
  issueBoard: IIssueBoardBase | IIssueBoardApi | IIssueBoardDBSchema;
  evaluationCategoryCapital: ICapitalLetter;
  afterTiltak?: boolean;
}

export interface IMatrixOutput {
  matrixItem: IMatrixItem | undefined;
  score: number;
}

const getRecordOrMapValue = <T>(value: unknown, key: string): T | undefined => {
  if (value instanceof Map) {
    return value.get(key);
  }

  if (value != null && typeof value === "object") {
    return (value as Record<string, T>)[key];
  }

  return undefined;
};

export const getMatrixItemAndScore = (args: IGetMatrixArgs): IMatrixOutput => {
  let score = 0;
  try {
    const risikoSection = args.issueBoard.risikoSection;
    const probabilityKey = args.afterTiltak
      ? args.issue.risiko?.probabilityAfterTiltak
      : args.issue.risiko?.probability;
    const consequenceKey = args.afterTiltak
      ? args.issue.risiko?.consequenceAfterTiltak?.[args.evaluationCategoryCapital]
      : args.issue.risiko?.consequence?.[args.evaluationCategoryCapital];
    if (probabilityKey == null || consequenceKey == null || risikoSection == null) {
      return {
        matrixItem: undefined,
        score: 0,
      };
    }
    const probability = getRecordOrMapValue<IRisikoProbability>(risikoSection.probabilites, probabilityKey);
    const evaluationCategory = getRecordOrMapValue<{ consequences: unknown; matrixConfig: IMatrixConfig }>(
      risikoSection.evaluationCategoriesConfig,
      args.evaluationCategoryCapital,
    );

    if (probability == null || evaluationCategory == null) {
      return {
        matrixItem: undefined,
        score: 0,
      };
    }

    const probabilityOrder = probability.order;
    const probabilityMatrixValue = probability.matrixValue;
    try {
      const consequence = getRecordOrMapValue<{ order: number; matrixValue: number }>(
        evaluationCategory.consequences,
        consequenceKey,
      );
      if (consequence == null) {
        return {
          matrixItem: undefined,
          score,
        };
      }
      const consequenceOrder = consequence.order;
      const consequenceMatrixValue = consequence.matrixValue;

      score = probabilityMatrixValue * consequenceMatrixValue;
      const matrix: IMatrixConfig = evaluationCategory.matrixConfig;
      return {
        matrixItem: matrix[consequenceOrder]?.[probabilityOrder],
        score,
      };
    } catch (error) {
      return {
        matrixItem: undefined,
        score,
      };
    }
  } catch (error) {
    console.error("failed to get matrix item", error);
    return {
      matrixItem: undefined,
      score: 0,
    };
  }
};

export type TidsfristBucket = "Ingen dato" | "Utløpt" | "I dag" | "I morgen" | "Denne uken" | "Neste uke" | "Fremtidig";

// Hjälpare: välj ett konkret due-datum som landar i rätt tidsfrist-bucket
export function computeDueForTidsfristBucket(targetColumn: string, current?: Date | string | null): Date | null {
  const today = startOfDay(new Date());
  const tomorrow = addDays(today, 1);
  const thisWeekEnd = endOfWeek(today);
  const nextWeekEnd = endOfWeek(addDays(today, 7));

  switch (targetColumn) {
    case "Ingen dato":
      return null;
    case "Utløpt":
      // en dag bakåt → försenad
      return endOfDay(addDays(today, -1));
    case "I dag":
      return endOfDay(today);
    case "I morgen":
      return endOfDay(tomorrow);
    case "Denne uken":
      return endOfDay(thisWeekEnd);
    case "Neste uke":
      return endOfDay(nextWeekEnd);
    case "Fremtidig":
      // första dagen efter nästa vecka (mån) som enkel standard
      return endOfDay(addDays(nextWeekEnd, 1));
    default:
      return current ? new Date(current) : null;
  }
}

// Hjälpare: klassificera tidsfrist-kolumn utifrån dates.due
export function classifyTidsfrist(due?: string | Date | null): string {
  if (due == null || due === "") return "Ingen dato";
  const dueDate = startOfDay(new Date(due));
  if (Number.isNaN(dueDate.getTime())) return "Ingen dato";

  const today = startOfDay(new Date());
  const tomorrow = addDays(today, 1);
  const thisWeekEnd = endOfWeek(today);
  const nextWeekStart = startOfIsoWeek(addDays(today, 7));
  const nextWeekEnd = endOfWeek(addDays(today, 7));

  if (dueDate < today) return "Utløpt";
  if (dueDate.getTime() === today.getTime()) return "I dag";
  if (dueDate.getTime() === tomorrow.getTime()) return "I morgen";
  if (dueDate > tomorrow && dueDate <= thisWeekEnd) return "Denne uken";
  if (dueDate >= nextWeekStart && dueDate <= nextWeekEnd) return "Neste uke";
  return "Fremtidig";
}
